import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Loader2, Newspaper, Eye, EyeOff, Image as ImageIcon, Bold, Italic, Heading, List, Link2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'Général' },
  { value: 'event', label: 'Événement' },
  { value: 'innovation', label: 'Innovation' },
  { value: 'industry', label: 'Industrie' },
];

function MediaPicker({ open, onClose, onSelect, token }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/gallery/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setImages(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, token]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Sélectionner une image</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto mt-2">
          {(() => {
            if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>;
            if (images.length === 0) return <p className="text-center text-[#6B7C93] py-8">Aucune image dans la bibliothèque</p>;
            return (
              <div className="grid grid-cols-4 gap-2">
                {images.map(img => (
                  <button
                    key={img.id}
                    onClick={() => { onSelect(`${BACKEND_URL}${img.url}`); onClose(); }}
                    className="rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F5A623] transition-colors focus:border-[#1E3A5F] focus:outline-none"
                    data-testid={`pick-media-${img.id}`}
                  >
                    <img src={`${BACKEND_URL}${img.url}`} alt={img.title} className="w-full h-24 object-cover" />
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

import DOMPurify from 'dompurify';

function RichEditor({ value, onChange }) {
  const insertMarkdown = (prefix, suffix = '') => {
    const textarea = document.getElementById('rich-editor-area');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end) || 'texte';
    const newText = value.substring(0, start) + prefix + selected + suffix + value.substring(end);
    onChange(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    }, 0);
  };

  const renderPreview = (text) => {
    if (!text) return '<p class="text-gray-400">Aperçu du contenu...</p>';
    let html = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mt-3 mb-1">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 underline" target="_blank">$1</a>')
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded my-2" />')
      .replace(/\n/g, '<br/>');
    return html;
  };

  return (
    <div className="border border-[#E1E8F0] rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-2 bg-[#F7F9FC] border-b border-[#E1E8F0]">
        <button type="button" onClick={() => insertMarkdown('**', '**')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Gras"><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => insertMarkdown('*', '*')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Italique"><Italic className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-[#E1E8F0] mx-1" />
        <button type="button" onClick={() => insertMarkdown('## ')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Titre"><Heading className="w-4 h-4" /></button>
        <button type="button" onClick={() => insertMarkdown('- ')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Liste"><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => insertMarkdown('[', '](url)')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Lien"><Link2 className="w-4 h-4" /></button>
        <button type="button" onClick={() => insertMarkdown('![image](', ')')} className="p-1.5 rounded hover:bg-[#E1E8F0] text-[#6B7C93]" title="Image"><ImageIcon className="w-4 h-4" /></button>
      </div>

      <Tabs defaultValue="edit" className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
          <TabsTrigger value="edit" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1E3A5F] data-[state=active]:bg-transparent px-4 py-2 text-sm">Éditer</TabsTrigger>
          <TabsTrigger value="preview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1E3A5F] data-[state=active]:bg-transparent px-4 py-2 text-sm">Aperçu</TabsTrigger>
        </TabsList>
        <TabsContent value="edit" className="mt-0">
          <textarea
            id="rich-editor-area"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={12}
            className="w-full p-4 text-sm border-0 focus:outline-none resize-none font-mono"
            placeholder="Utilisez **gras**, *italique*, ## titres, - listes, [lien](url), ![image](url)..."
            data-testid="news-content-input"
          />
        </TabsContent>
        <TabsContent value="preview" className="mt-0">
          <div
            className="p-4 prose prose-sm min-h-[200px] max-w-none text-[#4A5568]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(value)) }}
            data-testid="news-content-preview"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function ManageNews() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', image_url: '', category: 'general', is_published: false });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaPicker, setMediaPicker] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/news/all`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async () => {
    if (!form.title) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/news/${editId}`, form, { headers });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/news`, form, { headers });
        toast.success('Article créé');
      }
      setView('list');
      setForm({ title: '', excerpt: '', content: '', image_url: '', category: 'general', is_published: false });
      setEditId(null);
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title,
      excerpt: item.excerpt || '',
      content: item.content || '',
      image_url: item.image_url || '',
      category: item.category || 'general',
      is_published: item.is_published,
    });
    setEditId(item.id);
    setView('edit');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/news/${id}`, { headers });
      toast.success('Supprimé');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const togglePublish = async (item) => {
    try {
      await axios.put(`${API}/news/${item.id}`, { is_published: !item.is_published }, { headers });
      toast.success(item.is_published ? 'Article dépublié' : 'Article publié');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const openNew = () => {
    setForm({ title: '', excerpt: '', content: '', image_url: '', category: 'general', is_published: false });
    setEditId(null);
    setView('edit');
  };

  if (view === 'edit') {
    return (
      <div data-testid="news-editor">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => setView('list')} className="text-[#6B7C93]" data-testid="back-to-list-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Retour
          </Button>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">{editId ? 'Modifier' : 'Nouvel'} article</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main editor */}
          <div className="lg:col-span-2 space-y-5">
            <div>
              <Label className="text-sm font-semibold">Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 text-lg font-medium"
                placeholder="Titre de l'article..."
                data-testid="news-title-input"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Extrait</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                className="mt-1"
                placeholder="Résumé court visible dans la liste..."
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Contenu</Label>
              <div className="mt-1">
                <RichEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-[#1E3A5F] text-sm">Publication</h3>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Publier</Label>
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                    data-testid="news-publish-toggle"
                  />
                </div>
                <div>
                  <Label className="text-sm">Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="news-submit-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editId ? 'Mettre à jour' : 'Créer l\'article'}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-[#1E3A5F] text-sm">Image de couverture</h3>
                {form.image_url && (
                  <img src={form.image_url} alt="Couverture" className="w-full h-32 object-cover rounded-lg" />
                )}
                <div className="flex gap-2">
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="URL de l'image..."
                    className="text-sm flex-1"
                  />
                  <Button variant="outline" size="sm" onClick={() => setMediaPicker(true)} title="Bibliothèque" data-testid="open-media-picker">
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <MediaPicker
          open={mediaPicker}
          onClose={() => setMediaPicker(false)}
          onSelect={(url) => setForm({ ...form, image_url: url })}
          token={token}
        />
      </div>
    );
  }

  return (
    <div data-testid="manage-news">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Actualités</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} article(s)</p>
        </div>
        <Button onClick={openNew} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-news-btn">
          <Plus className="w-4 h-4 mr-2" />Nouvel article
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="cursor-pointer hover:bg-[#F7F9FC]" onClick={() => handleEdit(item)}>
                  <TableCell>
                    {item.image_url ? (
                      <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[#F0F2F5] flex items-center justify-center"><Newspaper className="w-4 h-4 text-[#94A3B8]" /></div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium max-w-[300px] truncate">{item.title}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded-full bg-[#F0F2F5] capitalize">{item.category}</span></TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item.is_published ? 'Publié' : 'Brouillon'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-[#6B7C93]">{new Date(item.created_at).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => togglePublish(item)} title={item.is_published ? 'Dépublier' : 'Publier'}>
                        {item.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-news-${item.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500" data-testid={`delete-news-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]"><Newspaper className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun article</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
